<template>
  <div ref="rootRef" class="ui-overflow-menu">
    <button
      type="button"
      :disabled="disabled"
      :class="['ui-overflow-menu__trigger', triggerClass, { 'is-open': isOpen }]"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="buttonLabel"
      :data-tippy-content="triggerTooltip || undefined"
      data-tippy-placement="top"
      :data-tippy-delay="triggerTooltip ? '2000,0' : undefined"
      @click.stop="toggleMenu"
    >
      <slot name="trigger">
        <i class="material-icons">more_vert</i>
      </slot>
    </button>

    <component :is="portal ? Teleport : 'div'" v-bind="portal ? { to: 'body' } : { class: 'ui-overflow-menu__inline-layer' }">
      <div
        v-if="isOpen"
        ref="panelRef"
        :class="[
          'ui-overflow-menu__panel',
          { 'ui-overflow-menu__panel--fixed': portal },
          portal ? '' : `ui-overflow-menu__panel--${align}`
        ]"
        :style="portal ? panelStyle : undefined"
        role="menu"
      >
        <button
          v-for="option in options"
          :key="option.key"
          type="button"
          role="menuitem"
          :disabled="Boolean(option.disabled)"
          :class="[
            'ui-overflow-menu__item',
            option.className,
            { 'is-danger': option.tone === 'danger' }
          ]"
          @click.stop="handleSelect(option.key)"
        >
          <i v-if="option.icon" class="material-icons">{{ option.icon }}</i>
          <span>{{ option.label }}</span>
        </button>
      </div>
    </component>
  </div>
</template>

<script setup lang="ts">
import { Teleport, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { onDocumentModalEvent } from '@/components/modal-events';
import type { OverflowMenuOption } from './OverflowMenu';

const props = withDefaults(defineProps<{
  options: OverflowMenuOption[];
  align?: 'left' | 'right';
  buttonLabel?: string;
  triggerClass?: string | string[] | Record<string, boolean>;
  triggerTooltip?: string;
  closeOnSelect?: boolean;
  disabled?: boolean;
  portal?: boolean;
  placement?: 'top' | 'bottom';
  offset?: number;
}>(), {
  align: 'right',
  buttonLabel: 'Open menu',
  triggerClass: '',
  triggerTooltip: '',
  closeOnSelect: true,
  disabled: false,
  portal: false,
  placement: 'bottom',
  offset: 6
});

const emit = defineEmits<{
  select: [key: string];
  open: [];
  close: [];
}>();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});
let removeModalHideListener: (() => void) | null = null;

function updatePanelPosition(): void {
  if (!props.portal || !rootRef.value || !panelRef.value) {
    return;
  }

  const triggerRect = rootRef.value.getBoundingClientRect();
  const panelRect = panelRef.value.getBoundingClientRect();
  const panelWidth = panelRect.width || panelRef.value.offsetWidth || 176;
  const panelHeight = panelRect.height || panelRef.value.offsetHeight || 0;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const edgeMargin = 12;

  const left = props.align === 'left'
    ? Math.min(Math.max(edgeMargin, triggerRect.left), viewportWidth - panelWidth - edgeMargin)
    : Math.min(Math.max(edgeMargin, triggerRect.right - panelWidth), viewportWidth - panelWidth - edgeMargin);

  const preferredTop = props.placement === 'top'
    ? triggerRect.top - panelHeight - props.offset
    : triggerRect.bottom + props.offset;

  const fallbackTop = props.placement === 'top'
    ? triggerRect.bottom + props.offset
    : triggerRect.top - panelHeight - props.offset;

  const fitsPreferred = props.placement === 'top'
    ? preferredTop >= edgeMargin
    : preferredTop + panelHeight <= viewportHeight - edgeMargin;

  const maxTop = Math.max(edgeMargin, viewportHeight - panelHeight - edgeMargin);
  const top = fitsPreferred
    ? preferredTop
    : Math.min(
      Math.max(edgeMargin, fallbackTop),
      maxTop
    );

  panelStyle.value = {
    position: 'fixed',
    left: `${Math.round(left)}px`,
    right: 'auto',
    top: `${Math.round(top)}px`,
    bottom: 'auto',
    zIndex: '2600',
    width: 'max-content',
    minWidth: '176px',
    maxWidth: `calc(100vw - ${edgeMargin * 2}px)`,
    maxHeight: `calc(100vh - ${edgeMargin * 2}px)`,
    overflowY: 'auto'
  };
}

function closeMenu(): void {
  if (!isOpen.value) {
    return;
  }

  isOpen.value = false;
  emit('close');
}

function openMenu(): void {
  if (isOpen.value) {
    return;
  }

  isOpen.value = true;
  emit('open');
  void nextTick(() => {
    updatePanelPosition();
    window.requestAnimationFrame(() => {
      updatePanelPosition();
    });
  });
}

function toggleMenu(): void {
  if (props.disabled) {
    return;
  }
  if (isOpen.value) {
    closeMenu();
    return;
  }

  openMenu();
}

function handleSelect(key: string): void {
  emit('select', key);
  if (props.closeOnSelect) {
    closeMenu();
  }
}

function handleDocumentPointerDown(event: PointerEvent): void {
  if (!rootRef.value) {
    return;
  }

  const target = event.target as Node | null;
  if (target && !rootRef.value.contains(target) && !panelRef.value?.contains(target)) {
    closeMenu();
  }
}

function handleDocumentKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeMenu();
  }
}

function handleViewportChange(): void {
  if (isOpen.value) {
    updatePanelPosition();
  }
}

function handleModalHide(): void {
  closeMenu();
}

watch(() => props.disabled, (isDisabled) => {
  if (isDisabled) {
    closeMenu();
  }
});

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  document.addEventListener('keydown', handleDocumentKeydown);
      removeModalHideListener = onDocumentModalEvent('hide.bs.modal', handleModalHide);
  window.addEventListener('resize', handleViewportChange, true);
  window.addEventListener('scroll', handleViewportChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown);
  document.removeEventListener('keydown', handleDocumentKeydown);
  removeModalHideListener?.();
  removeModalHideListener = null;
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

defineExpose({
  closeMenu,
  openMenu,
  panelRef,
  rootRef
});
</script>
